import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import { Add, ConfirmationNumber, AccessTime, CheckCircle, Visibility, Business, ArrowForward } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@shared/auth/AuthContext'
import { ticketService } from '@shared/services/ApiClient'
import { OrganizationService } from '@shared/services/OrganizationService'
import { colors } from '@shared/styles/theme'
import { Organization } from '@shared/types/entities'

function CustomerDashboard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [apiStatus, setApiStatus] = useState<'loading' | 'success' | 'error' | 'idle'>('idle')
  const [ticketStats, setTicketStats] = useState({
    total: 0,
    open: 0,
    closed: 0
  })
  const [oversightOrganizations, setOversightOrganizations] = useState<Organization[]>([])
  const [loadingOrganizations, setLoadingOrganizations] = useState(false)

  const isOversightUser = user?.role === 'Oversight' || (user?.oversightChildSiteIds && user.oversightChildSiteIds.length > 0)

  useEffect(() => {
    loadTicketStats()
    if (isOversightUser && user?.userId) {
      loadOversightOrganizations()
    }
  }, [isOversightUser, user?.userId])

  const loadTicketStats = async () => {
    setApiStatus('loading')
    try {
      // Use getAllTickets to respect organization visibility settings
      // Backend will automatically show org tickets if DefaultTicketVisibility = "Organization"
      const tickets = await ticketService.getAllTickets(1, 100)
      const open = tickets.data.filter((t: any) => t.status !== 'Closed').length
      const closed = tickets.data.filter((t: any) => t.status === 'Closed').length

      setTicketStats({
        total: tickets.totalCount,
        open,
        closed
      })
      setApiStatus('success')
    } catch (error) {
      console.error('Failed to load ticket stats:', error)
      setApiStatus('error')
    }
  }

  const loadOversightOrganizations = async () => {
    if (!user?.userId) return

    try {
      setLoadingOrganizations(true)
      const data = await OrganizationService.getOrganizationsForOversight(user.userId)
      setOversightOrganizations(data.filter(org => org.isActive))
    } catch (err: any) {
      console.error('Failed to load oversight organizations:', err)
    } finally {
      setLoadingOrganizations(false)
    }
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Welcome back, {user?.firstName || user?.fullName || 'Customer'}!
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Here's an overview of your support tickets
      </Typography>

      {/* Oversight Sites Indicator */}
      {isOversightUser && oversightOrganizations.length > 0 && (
        <Alert
          severity="info"
          icon={<Visibility />}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Oversight View - Monitoring {oversightOrganizations.length} {oversightOrganizations.length === 1 ? 'Site' : 'Sites'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              You're viewing aggregated data from all sites you oversee:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              {oversightOrganizations.map((org) => (
                <Chip
                  key={org.organizationId}
                  icon={<Business />}
                  label={org.name}
                  size="small"
                  variant="outlined"
                  sx={{ backgroundColor: 'background.paper' }}
                />
              ))}
            </Stack>
          </Box>
        </Alert>
      )}

      {isOversightUser && loadingOrganizations && (
        <Alert severity="info" sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading oversight sites...</Typography>
          </Box>
        </Alert>
      )}

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        {!isOversightUser && (
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate('/customer/create-ticket')}
            sx={{ mr: 2 }}
          >
            Create New Ticket
          </Button>
        )}
        <Button
          variant="outlined"
          size="large"
          startIcon={<ConfirmationNumber />}
          onClick={() => navigate('/customer/tickets')}
        >
          View All Tickets
        </Button>
      </Box>

      {/* Ticket Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ConfirmationNumber sx={{ fontSize: 40, color: colors.blue, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : ticketStats.total}
              </Typography>
              <Typography color="text.secondary">Total Tickets</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccessTime sx={{ fontSize: 40, color: colors.orange, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : ticketStats.open}
              </Typography>
              <Typography color="text.secondary">Open Tickets</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: colors.greenDark, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : ticketStats.closed}
              </Typography>
              <Typography color="text.secondary">Resolved Tickets</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Oversight Sites Breakdown */}
      {isOversightUser && oversightOrganizations.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Sites Overview
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Site Name</strong></TableCell>
                  <TableCell align="center"><strong>Total Tickets</strong></TableCell>
                  <TableCell align="center"><strong>Open Tickets</strong></TableCell>
                  <TableCell align="center"><strong>Users</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {oversightOrganizations.map((org: any) => (
                  <TableRow key={org.childSiteId || org.organizationId} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {org.name}
                          </Typography>
                          {org.parentOrganizationName && (
                            <Typography variant="caption" color="text.secondary">
                              {org.parentOrganizationName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={org.ticketCount || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={org.openTicketCount || 0}
                        size="small"
                        color={org.openTicketCount > 0 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{org.userCount || 0}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        endIcon={<ArrowForward />}
                        onClick={() => navigate(`/customer/tickets?organizationId=${org.childSiteId || org.organizationId}`)}
                      >
                        View Tickets
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell align="center">
                    <Chip
                      label={oversightOrganizations.reduce((sum: number, org: any) => sum + (org.ticketCount || 0), 0)}
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={oversightOrganizations.reduce((sum: number, org: any) => sum + (org.openTicketCount || 0), 0)}
                      size="small"
                      color="warning"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <strong>{oversightOrganizations.reduce((sum: number, org: any) => sum + (org.userCount || 0), 0)}</strong>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* API Status */}
      {apiStatus === 'error' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Unable to load ticket statistics. You can still create and view tickets.
        </Alert>
      )}

      {apiStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          All systems operational! Your tickets are synchronized.
        </Alert>
      )}
    </Box>
  )
}

export default CustomerDashboard